import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faTimes,
  faPlus,
  faSearch,
  faSave,
  faChevronLeft,
  faHeartbeat, faStethoscope, faUserMd, faWheelchair, faAmbulance,
  faHospital, faSyringe, faPills, faBriefcaseMedical, faNotesMedical,
  faPrescriptionBottleAlt, faLungs, faBrain, faTooth, faBone,
  faVirus, faVial, faDna, faMicroscope, faXRay, faThermometerHalf,
  faLaptopMedical, faMobileAlt, faPhoneAlt, faClinicMedical
} from '@fortawesome/free-solid-svg-icons';
import { useBreadcrumb } from '../../../contexts/BreadcrumbContext';
import Heading from '../../../components/Common/Heading/Heading';
import careProgramService from '../../../services/careProgramService';
import {
  clampCptInput,
  parseCptCodesFromInput,
  getCptCodeValidationError,
  validateCptCodeList,
  normalizeCptCode,
} from '../../../utils/cptCodeValidation';
import {
  clearFieldError,
  isBlank,
  reportValidationErrors,
} from '../../../utils/formValidation';
import './AddCareProgram.css';
import { matchesWordStartAny } from '../../../utils/searchMatch';

const BADGE_COLORS = [
  '#263651', // Navy (Brand)
  '#1e40af', // Blue
  '#065f46', // Emerald
  '#991b1b', // Red
  '#92400e', // Amber
  '#3730a3', // Indigo
  '#134e4a', // Teal
  '#1e293b'  // Slate
];

const MEDICAL_ICONS = [
  { name: 'faHeartbeat', icon: faHeartbeat },
  { name: 'faStethoscope', icon: faStethoscope },
  { name: 'faUserMd', icon: faUserMd },
  { name: 'faWheelchair', icon: faWheelchair },
  { name: 'faAmbulance', icon: faAmbulance },
  { name: 'faHospital', icon: faHospital },
  { name: 'faSyringe', icon: faSyringe },
  { name: 'faPills', icon: faPills },
  { name: 'faBriefcaseMedical', icon: faBriefcaseMedical },
  { name: 'faNotesMedical', icon: faNotesMedical },
  { name: 'faPrescriptionBottleAlt', icon: faPrescriptionBottleAlt },
  { name: 'faLungs', icon: faLungs },
  { name: 'faBrain', icon: faBrain },
  { name: 'faTooth', icon: faTooth },
  { name: 'faBone', icon: faBone },
  { name: 'faVirus', icon: faVirus },
  { name: 'faVial', icon: faVial },
  { name: 'faDna', icon: faDna },
  { name: 'faMicroscope', icon: faMicroscope },
  { name: 'faXRay', icon: faXRay },
  { name: 'faThermometerHalf', icon: faThermometerHalf },
  { name: 'faLaptopMedical', icon: faLaptopMedical },
  { name: 'faMobileAlt', icon: faMobileAlt },
  { name: 'faPhoneAlt', icon: faPhoneAlt },
  { name: 'faClinicMedical', icon: faClinicMedical }
];

const PROGRAM_CATEGORIES = [
  'Remote Monitoring',
  'Chronic Care',
  'Behavioral Health',
  'Transitional Care',
  'Preventive Care',
  'Cardiac',
  'Other'
];

const REIMBURSEMENT_TYPES = [
  'Per patient monthly',
  'Per service',
  'Time-based',
  'Bundled payment'
];

const ICD10_CATEGORIES = [
  'Diabetes',
  'Hypertension',
  'Heart disease',
  'Mental health'
];

const CONSENT_OPTIONS = [
  'Written or verbal',
  'Written only',
  'Verbal only',
  'None required'
];

const normalizeConsentRequired = (value) => {
  if (!value) return '';
  if (value === 'Written + verbal') return 'Written or verbal';
  return CONSENT_OPTIONS.includes(value) ? value : '';
};

const normalizeReimbursementType = (value) => (
  REIMBURSEMENT_TYPES.includes(value) ? value : ''
);

const MOCK_ICD10_CODES = [
  { code: 'E11.9', diagnosis: 'Type 2 diabetes mellitus without complications', category: 'Diabetes' },
  { code: 'E10.9', diagnosis: 'Type 1 diabetes mellitus without complications', category: 'Diabetes' },
  { code: 'I10', diagnosis: 'Essential (primary) hypertension', category: 'Hypertension' },
  { code: 'I11.9', diagnosis: 'Hypertensive heart disease without heart failure', category: 'Hypertension' },
  { code: 'I25.10', diagnosis: 'Atherosclerotic heart disease of native coronary artery', category: 'Heart disease' },
  { code: 'I50.9', diagnosis: 'Heart failure, unspecified', category: 'Heart disease' },
  { code: 'F32.9', diagnosis: 'Major depressive disorder, single episode', category: 'Mental health' },
  { code: 'F41.1', diagnosis: 'Generalized anxiety disorder', category: 'Mental health' }
];

const LoopingSelect = ({ name, value, onChange, options, placeholder, error }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const containerRef = useRef(null);
  const listRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    if (isOpen) {
      const idx = value ? options.indexOf(value) : -1;
      setHighlightedIndex(idx);
    }
  }, [isOpen, value, options]);

  const handleKeyDown = (e) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      if (!isOpen) setIsOpen(true);
      setHighlightedIndex(prev => {
        const next = prev === options.length - 1 ? -1 : prev + 1;
        scrollToIndex(next);
        return next;
      });
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      if (!isOpen) setIsOpen(true);
      setHighlightedIndex(prev => {
        const next = prev <= -1 ? options.length - 1 : prev - 1;
        scrollToIndex(next);
        return next;
      });
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (isOpen && highlightedIndex >= -1) {
        onChange({ target: { name, value: highlightedIndex === -1 ? '' : options[highlightedIndex] } });
        setIsOpen(false);
      } else {
        setIsOpen(true);
      }
    } else if (e.key === 'Escape') {
      setIsOpen(false);
    }
  };

  const scrollToIndex = (index) => {
    if (listRef.current) {
      const items = listRef.current.children;
      const target = items[index + 1]; // +1 for placeholder
      if (target) {
        target.scrollIntoView({ block: 'nearest' });
      }
    }
  };

  const displayValue = options.includes(value) ? value : '';

  return (
    <div 
      ref={containerRef}
      style={{ position: 'relative', width: '100%' }}
      onKeyDown={handleKeyDown}
    >
      <div 
        tabIndex={0}
        onClick={() => setIsOpen(!isOpen)}
        style={{
          width: '100%',
          padding: '10px 14px',
          border: error ? '1px solid #dc2626' : isOpen ? '1px solid var(--ev-blue-accent)' : '1px solid #e2e8f0',
          borderRadius: '8px',
          fontSize: '14px',
          backgroundColor: '#ffffff',
          color: displayValue ? '#1e293b' : '#94a3b8',
          cursor: 'pointer',
          outline: 'none',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          boxShadow: isOpen ? '0 0 0 3px var(--ev-focus-shadow)' : 'none',
          fontFamily: 'Inter, sans-serif',
          transition: 'border-color 0.2s, box-shadow 0.2s'
        }}
      >
        <span>{displayValue || placeholder}</span>
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#475569" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <polyline points="6 9 12 15 18 9"></polyline>
        </svg>
      </div>

      {isOpen && (
        <div 
          ref={listRef}
          style={{
          position: 'absolute',
          top: '100%',
          left: 0,
          right: 0,
          marginTop: '4px',
          backgroundColor: '#ffffff',
          border: '1px solid #e2e8f0',
          borderRadius: '6px',
          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
          zIndex: 1000,
          maxHeight: '250px',
          overflowY: 'auto',
          padding: '4px 0'
        }}>
          <div
            onClick={() => {
              onChange({ target: { name, value: '' } });
              setIsOpen(false);
            }}
            onMouseEnter={() => setHighlightedIndex(-1)}
            style={{
              padding: '8px 14px',
              cursor: 'default',
              fontSize: '14px',
              backgroundColor: highlightedIndex === -1 ? '#2563eb' : 'transparent',
              color: highlightedIndex === -1 ? '#ffffff' : '#1e293b'
            }}
          >
            {placeholder}
          </div>
          {options.map((opt, idx) => (
            <div
              key={opt}
              onClick={() => {
                onChange({ target: { name, value: opt } });
                setIsOpen(false);
              }}
              onMouseEnter={() => setHighlightedIndex(idx)}
              style={{
                padding: '8px 14px',
                cursor: 'default',
                fontSize: '14px',
                backgroundColor: highlightedIndex === idx ? '#2563eb' : 'transparent',
                color: highlightedIndex === idx ? '#ffffff' : '#1e293b'
              }}
            >
              {opt}
            </div>
          ))}
        </div>
      )}
      <input type="hidden" name={name} value={displayValue} />
      {error && <p className="acp-field-error">{error}</p>}
    </div>
  );
};

const AddCareProgram = () => {
  const navigate = useNavigate();
  const { updateBreadcrumbs, clearBreadcrumbs } = useBreadcrumb();
  const { id } = useParams();
  const isEditMode = !!id;
  const [isLoading, setIsLoading] = useState(false);
  const icd10Ref = useRef(null);

  const defaultFormData = {
    shortName: '',
    category: '',
    fullName: '',
    description: '',
    badgeColor: BADGE_COLORS[0],
    icon: 'faHeartbeat',
    cptCodeList: [],
    icd10CodeList: [],
    reimbursementType: '',
    avgReimbursement: '',
    minEnrollment: '',
    consentRequired: '',
  };

  const [formData, setFormData] = useState(defaultFormData);
  const [originalFormData, setOriginalFormData] = useState(null);
  const [fieldErrors, setFieldErrors] = useState({});

  const [currentCpt, setCurrentCpt] = useState('');
  const [cptError, setCptError] = useState('');
  const [currentIcd10, setCurrentIcd10] = useState('');
  const [icd10Category, setIcd10Category] = useState('All categories');
  const [showIcd10Results, setShowIcd10Results] = useState(false);
  const [minEnrollmentError, setMinEnrollmentError] = useState('');

  const filteredIcd10Results = React.useMemo(() => {
    return MOCK_ICD10_CODES.filter(item => {
      const matchesCategory = icd10Category === 'All categories' || item.category === icd10Category;
      const matchesSearch = matchesWordStartAny([item.code, item.diagnosis], currentIcd10);
      return matchesCategory && matchesSearch;
    });
  }, [currentIcd10, icd10Category]);

  // Close ICD-10 dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (icd10Ref.current && !icd10Ref.current.contains(e.target)) {
        setShowIcd10Results(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    if (isEditMode) {
      const fetchProgram = async () => {
        setIsLoading(true);
        try {
          const response = await careProgramService.getCareProgramById(id);
          if (response.success) {
            const data = response.data;
            const loaded = {
              shortName: data.short_name,
              category: data.category,
              fullName: data.full_name,
              description: data.description || '',
              badgeColor: data.badge_color || BADGE_COLORS[0],
              icon: data.icon || 'faHeartbeat',
              cptCodeList: typeof data.cpt_codes === 'string' ? JSON.parse(data.cpt_codes) : (data.cpt_codes || []),
              icd10CodeList: typeof data.icd10_codes === 'string' ? JSON.parse(data.icd10_codes) : (data.icd10_codes || []),
              reimbursementType: normalizeReimbursementType(data.reimbursement_type),
              avgReimbursement: data.avg_reimbursement || '',
              minEnrollment: data.min_enrollment ? String(data.min_enrollment).replace(/\D/g, '') : '',
              consentRequired: normalizeConsentRequired(data.consent_required)
            };
            setFormData(loaded);
            setOriginalFormData(loaded);
          }
        } catch (error) {
          console.error('Error fetching care program:', error);
          toast.error('Failed to fetch care program details');
        } finally {
          setIsLoading(false);
        }
      };
      fetchProgram();
    }
  }, [id, isEditMode]);

  useEffect(() => {
    updateBreadcrumbs([
      { label: 'Dashboard', path: '/dashboard' },
      { label: 'Settings', path: '/settings' },
      { label: 'Care Programs', path: '/care-programs' },
      { label: isEditMode ? 'Edit Care Program' : 'Add Care Program', path: isEditMode ? `/care-programs/edit/${id}` : '/care-programs/add' }
    ]);
    return () => clearBreadcrumbs();
  }, [updateBreadcrumbs, clearBreadcrumbs, isEditMode, id]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    clearFieldError(setFieldErrors, name);
    if (name === 'minEnrollment') {
      const numericValue = value.replace(/\D/g, '');
      setFormData(prev => ({ ...prev, [name]: numericValue }));

      if (numericValue !== '' && parseInt(numericValue, 10) < 30) {
        setMinEnrollmentError('Minimum enrollment period must be at least 30 days');
      } else {
        setMinEnrollmentError('');
      }
      return;
    }
    setFormData(prev => ({ ...prev, [name]: value }));
    if (name === 'reimbursementType' || name === 'consentRequired' || name === 'category') {
      clearFieldError(setFieldErrors, name);
    }
  };

  const handleColorSelect = (color) => {
    setFormData(prev => ({ ...prev, badgeColor: color }));
  };

  const handleIconSelect = (iconName) => {
    setFormData(prev => ({ ...prev, icon: iconName }));
  };

  const handleCptChange = (e) => {
    const rawValue = clampCptInput(e.target.value);
    setCurrentCpt(rawValue);
    clearFieldError(setFieldErrors, 'cptCodeList');

    if (!rawValue) {
      setCptError('');
      return;
    }

    const segments = rawValue.split(',').map((s) => s.trim()).filter(Boolean);
    for (const segment of segments) {
      if (segment.length === 5) {
        const err = getCptCodeValidationError(segment);
        if (err) {
          setCptError(err);
          return;
        }
      }
    }
    setCptError('');
  };

  const handleAddCpt = () => {
    if (!currentCpt.trim()) {
      setCptError('CPT code is required');
      return;
    }
    const newCodes = parseCptCodesFromInput(currentCpt);
    if (newCodes.length === 0) return;

    for (const code of newCodes) {
      const err = getCptCodeValidationError(code);
      if (err) {
        setCptError(err);
        return;
      }
    }

    setCptError('');
    clearFieldError(setFieldErrors, 'cptCodeList');
    setFormData((prev) => {
      const updatedList = [...prev.cptCodeList];
      newCodes.forEach((code) => {
        const normalized = normalizeCptCode(code);
        if (!updatedList.includes(normalized)) updatedList.push(normalized);
      });
      return { ...prev, cptCodeList: updatedList };
    });
    setCurrentCpt('');
  };

  const handleRemoveCpt = (code) => {
    setFormData(prev => ({
      ...prev,
      cptCodeList: prev.cptCodeList.filter(c => c !== code)
    }));
  };

  const handleSelectIcd10 = (item) => {
    const tag = `${item.code} - ${item.diagnosis}`;
    if (!formData.icd10CodeList.includes(tag)) {
      setFormData(prev => ({
        ...prev,
        icd10CodeList: [...prev.icd10CodeList, tag]
      }));
    }
    setCurrentIcd10('');
    setShowIcd10Results(false);
  };

  const handleRemoveIcd10 = (tag) => {
    setFormData(prev => ({
      ...prev,
      icd10CodeList: prev.icd10CodeList.filter(t => t !== tag)
    }));
  };

  const hasChanges = useCallback(() => {
    if (!isEditMode || !originalFormData) return true;
    return JSON.stringify(formData) !== JSON.stringify(originalFormData);
  }, [formData, originalFormData, isEditMode]);

  const validateForm = () => {
    const nextErrors = {};

    if (isBlank(formData.shortName)) {
      nextErrors.shortName = 'Short name is required';
    }
    if (isBlank(formData.category)) {
      nextErrors.category = 'Program category is required';
    }
    if (isBlank(formData.fullName)) {
      nextErrors.fullName = 'Full program name is required';
    }
    if (!formData.cptCodeList.length) {
      nextErrors.cptCodeList = 'At least one CPT code is required';
    }
    if (!formData.reimbursementType) {
      nextErrors.reimbursementType = 'Please select a reimbursement type';
    }
    if (!formData.consentRequired) {
      nextErrors.consentRequired = 'Please select a patient consent requirement';
    }

    return reportValidationErrors(setFieldErrors, nextErrors, toast);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (isEditMode && !hasChanges()) {
      toast.info('No changes detected. Please make changes before updating.');
      return;
    }
    if (!validateForm()) {
      return;
    }
    if (formData.minEnrollment !== '' && parseInt(formData.minEnrollment, 10) < 30) {
      toast.error('Minimum enrollment period must be at least 30 days');
      setMinEnrollmentError('Minimum enrollment period must be at least 30 days');
      return;
    }
    const cptListError = validateCptCodeList(formData.cptCodeList);
    if (cptListError) {
      toast.error(cptListError);
      setCptError(cptListError);
      return;
    }
    setIsLoading(true);
    try {
      if (isEditMode) {
        await careProgramService.updateCareProgram(id, formData);
        toast.success('Care program updated successfully');
      } else {
        await careProgramService.createCareProgram(formData);
        toast.success('Care program created successfully');
      }
      navigate('/care-programs');
    } catch (error) {
      console.error('Error saving care program:', error);
      toast.error(error.response?.data?.message || 'Failed to save care program');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="list-screen-page-container acp-page">
      <div className="acp-header">
        <div className="acp-header-left">
          <Heading title={isEditMode ? 'Edit Care Program' : 'Add Care Program'} level="h2" />
          <p className="acp-subtitle">{isEditMode ? 'Update care program configuration' : 'Configure a new care program for practice enrollment'}</p>
        </div>
      </div>

      <form className="acp-form" onSubmit={handleSave} noValidate>
        {/* Program Identity Section */}
        <div className="acp-section">
          <Heading title="Program Identity" level="h3" className="acp-section-title" />
          <div className="acp-grid">
            <div className="acp-field">
              <label>Short Name <span className="required">*</span></label>
              <input
                type="text"
                name="shortName"
                placeholder="e.g., RPM"
                value={formData.shortName}
                onChange={handleChange}
                className={fieldErrors.shortName ? 'acp-input-error' : ''}
              />
              {fieldErrors.shortName && <p className="acp-field-error">{fieldErrors.shortName}</p>}
            </div>
            <div className="acp-field">
              <label>Program Category <span className="required">*</span></label>
              <LoopingSelect
                name="category"
                value={formData.category}
                onChange={handleChange}
                options={PROGRAM_CATEGORIES}
                placeholder="Select category"
                error={fieldErrors.category}
              />
            </div>
          </div>
          <div className="acp-field full-width">
            <label>Full Program Name <span className="required">*</span></label>
            <input
              type="text"
              name="fullName"
              placeholder="e.g., Remote Patient Monitoring"
              value={formData.fullName}
              onChange={handleChange}
              className={fieldErrors.fullName ? 'acp-input-error' : ''}
            />
            {fieldErrors.fullName && <p className="acp-field-error">{fieldErrors.fullName}</p>}
          </div>
          <div className="acp-field full-width">
            <label>Description</label>
            <textarea
              name="description"
              className="acp-description-textarea"
              placeholder="Brief description of what this program covers..."
              value={formData.description}
              onChange={handleChange}
              maxLength={850}
              rows={8}
              style={{ resize: 'none' }}
            />
            <span className="acp-char-count">{formData.description.length}/850 characters</span>
          </div>
          <div className="acp-field full-width">
            <label>Badge Color</label>
            <div className="acp-color-grid">
              {BADGE_COLORS.map(color => (
                <div
                  key={color}
                  className={`acp-color-circle ${formData.badgeColor === color ? 'active' : ''}`}
                  style={{ backgroundColor: color }}
                  onClick={() => handleColorSelect(color)}
                />
              ))}
            </div>
          </div>
          <div className="acp-field full-width">
            <label>Program Icon</label>
            <div className="acp-icon-grid">
              {MEDICAL_ICONS.map(iconObj => (
                <div
                  key={iconObj.name}
                  className={`acp-icon-circle ${formData.icon === iconObj.name ? 'active' : ''}`}
                  style={{ color: formData.icon === iconObj.name ? formData.badgeColor : '#64748b', borderColor: formData.icon === iconObj.name ? formData.badgeColor : '#e2e8f0' }}
                  onClick={() => handleIconSelect(iconObj.name)}
                >
                  <FontAwesomeIcon icon={iconObj.icon} size="lg" />
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Billing & Coding Section */}
        <div className="acp-section">
          <Heading title="Billing & Coding" level="h3" className="acp-section-title" />
          <div className="acp-field full-width">
            <label>CPT Codes <span className="required">*</span></label>
            <div className="acp-input-with-btn">
              <input
                type="text"
                placeholder="Enter CPT code (e.g. 99213 or 0256T) — separate multiple with commas"
                value={currentCpt}
                maxLength={currentCpt.includes(',') ? currentCpt.lastIndexOf(',') + 6 : 5}
                onChange={handleCptChange}
                onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), handleAddCpt())}
                className={cptError || fieldErrors.cptCodeList ? 'acp-input-error' : ''}
              />
              <button
                type="button"
                className="acp-add-inner-btn"
                onClick={handleAddCpt}
              >
                Add
              </button>
            </div>
            {cptError && <p className="acp-field-error">{cptError}</p>}
            {!cptError && fieldErrors.cptCodeList && (
              <p className="acp-field-error">{fieldErrors.cptCodeList}</p>
            )}
            {formData.cptCodeList.length > 0 && (
              <div className="acp-tag-container">
                {formData.cptCodeList.map(code => (
                  <div key={code} className="acp-tag">
                    {code}
                    <FontAwesomeIcon icon={faTimes} className="acp-tag-remove" onClick={() => handleRemoveCpt(code)} />
                  </div>
                ))}
              </div>
            )}
          </div>
          <div className="acp-grid">
            <div className="acp-field">
              <label>Reimbursement Type <span className="required">*</span></label>
              <LoopingSelect
                name="reimbursementType"
                value={formData.reimbursementType}
                onChange={handleChange}
                options={REIMBURSEMENT_TYPES}
                placeholder="Select type"
                error={fieldErrors.reimbursementType}
              />
            </div>
            <div className="acp-field">
              <label>Avg. Monthly Reimbursement ($)</label>
              <input
                type="number"
                name="avgReimbursement"
                placeholder="e.g., 85"
                value={formData.avgReimbursement}
                onChange={(e) => setFormData(prev => ({ ...prev, avgReimbursement: e.target.value }))}
                min="0"
                step="0.01"
              />
            </div>
          </div>
        </div>

        {/* Clinical Requirements Section */}
        <div className="acp-section">
          <Heading title="Clinical Requirements" level="h3" className="acp-section-title" />
          <div className="acp-field full-width">
            <label>ICD-10 Diagnosis Codes</label>
            <p className="acp-field-sub">Search and select qualifying diagnosis codes</p>
            <div className="acp-input-with-select" ref={icd10Ref}>
              <div className="acp-search-input-wrapper">
                <FontAwesomeIcon icon={faSearch} className="acp-search-icon" />
                <input
                  type="text"
                  placeholder="Search by code or diagnosis name..."
                  value={currentIcd10}
                  onChange={(e) => { setCurrentIcd10(e.target.value); setShowIcd10Results(true); }}
                  onFocus={() => setShowIcd10Results(true)}
                />
                {showIcd10Results && (
                  <div className="acp-dropdown-results">
                    {filteredIcd10Results.length > 0 ? (
                      filteredIcd10Results.map(item => (
                        <div
                          key={item.code}
                          className="acp-dropdown-item"
                          onMouseDown={(e) => { e.preventDefault(); handleSelectIcd10(item); }}
                        >
                          <span className="acp-item-code">{item.code}</span>
                          <span className="acp-item-desc">{item.diagnosis}</span>
                          <span className="acp-item-cat">{item.category}</span>
                        </div>
                      ))
                    ) : (
                      <div className="acp-no-results">No codes found for this category</div>
                    )}
                  </div>
                )}
              </div>
              <select
                className="acp-inner-select"
                value={icd10Category}
                onChange={(e) => {
                  setIcd10Category(e.target.value);
                }}
              >
                <option>All categories</option>
                {ICD10_CATEGORIES.map(cat => <option key={cat} value={cat}>{cat}</option>)}
              </select>
            </div>
            {formData.icd10CodeList.length > 0 && (
              <div className="acp-tag-container">
                {formData.icd10CodeList.map(tag => (
                  <div key={tag} className="acp-tag icd10">
                    {tag}
                    <FontAwesomeIcon icon={faTimes} className="acp-tag-remove" onClick={() => handleRemoveIcd10(tag)} />
                  </div>
                ))}
              </div>
            )}
          </div>
          <div className="acp-grid">
            <div className="acp-field">
              <label>Min. Enrollment Period (Days)</label>
              <input
                type="text"
                name="minEnrollment"
                placeholder="e.g., 30"
                value={formData.minEnrollment}
                onChange={handleChange}
                style={{ borderColor: minEnrollmentError ? '#dc2626' : undefined }}
              />
              {minEnrollmentError && <p className="acp-field-error" style={{ color: '#dc2626', fontSize: '13px', marginTop: '6px' }}>{minEnrollmentError}</p>}
            </div>
            <div className="acp-field">
              <label>Patient Consent Required <span className="required">*</span></label>
              <LoopingSelect
                name="consentRequired"
                value={formData.consentRequired}
                onChange={handleChange}
                options={CONSENT_OPTIONS}
                placeholder="Select consent"
                error={fieldErrors.consentRequired}
              />
            </div>
          </div>
          {/* <div className="acp-field full-width">
            <label>Device Types (if applicable)</label>
            <input 
              type="text" 
              name="deviceTypes" 
              placeholder="e.g., Blood pressure monitor, glucometer" 
              value={formData.deviceTypes}
              onChange={handleChange}
            />
          </div> */}
          {/* <div className="acp-field full-width">
            <label>Care Team Roles</label>
            <input 
              type="text" 
              name="careTeamRoles" 
              placeholder="e.g., Care coordinator, RN, physician" 
              value={formData.careTeamRoles}
              onChange={handleChange}
            />
          </div> */}
        </div>

        {/* Form Actions */}
        <div className="acp-form-actions">
          <div className="acp-btns">
            <button type="button" className="acp-cancel-btn" onClick={() => navigate('/care-programs')}>Cancel</button>
            <button
              type="submit"
              className="acp-save-btn"
              disabled={isLoading || (isEditMode && !hasChanges())}
              title={isEditMode && !hasChanges() ? 'No changes to save' : ''}
            >
              <FontAwesomeIcon icon={isLoading ? faPlus : faSave} spin={isLoading} /> {isLoading ? (isEditMode ? 'Updating...' : 'Saving...') : (isEditMode ? 'Update Program' : 'Save Program')}
            </button>
          </div>
        </div>
      </form>
    </div>
  );
};

export default AddCareProgram;
